/**
 * Small pure helpers for the sea HUD's ship instruments (speed + wind) and the
 * minimap tap-to-sail autopilot. Kept dependency-free and side-effect-free so
 * they can be unit-tested in isolation (navmath.test.ts). CLIENT-ONLY, cosmetic:
 * the autopilot only synthesises a rudder value that flows through the SAME
 * C_INPUT path a thumb would — the server stays fully authoritative, zero wire.
 */

/** Sim speed is metres/second; the gauge reads knots like a real ship's log. */
export const KNOTS_PER_MS = 1.94384;

/** Convert a sim speed (m/s) to knots. */
export function msToKnots(mps: number): number {
  return Math.max(0, mps) * KNOTS_PER_MS;
}

/** Format a sim speed (m/s) as a one-decimal knots readout, e.g. 4.32 → "8.4". */
export function formatKnots(mps: number): string {
  return msToKnots(mps).toFixed(1);
}

/** Wrap an angle to (-π, π]. */
export function wrapPi(a: number): number {
  const twoPi = Math.PI * 2;
  a = a % twoPi;
  if (a > Math.PI) a -= twoPi;
  else if (a <= -Math.PI) a += twoPi;
  return a;
}

/**
 * Point-of-sail bucket from the sim's alignment term `cos(heading - windDir)`
 * (+1 running dead downwind = fastest, −1 in irons = slowest). Mirrors the
 * arcade `windFactor` in @piratas/sim so the label always matches the real speed
 * the ship is getting. Returns a short label + an accent colour for the gauge.
 */
export interface PointOfSail { label: string; color: string; }
export function pointOfSail(alignment: number): PointOfSail {
  if (alignment >= 0.66) return { label: 'RUNNING', color: '#54e6a0' };
  if (alignment >= 0.2) return { label: 'BROAD REACH', color: '#9fe07a' };
  if (alignment > -0.2) return { label: 'BEAM REACH', color: '#e8c074' };
  if (alignment > -0.66) return { label: 'CLOSE HAULED', color: '#ff9a5a' };
  return { label: 'IN IRONS', color: '#ff6b5a' };
}

/**
 * Autopilot rudder toward a waypoint — the tap-to-sail "helmsman". Pure function
 * of the ship's (x, y, heading) and the target (tx, ty): returns a rudder in
 * [-1, 1] that steers the bow onto the bearing to the target. Proportional with a
 * small dead-zone so a ship already pointed at the mark holds a steady helm
 * instead of hunting. Only the LEME is synthesised — sail/throttle stay with the
 * player, exactly like a real quartermaster taking the wheel.
 */
export function autopilotRudder(x: number, y: number, heading: number, tx: number, ty: number): number {
  const desired = Math.atan2(ty - y, tx - x);
  const err = wrapPi(desired - heading);
  if (Math.abs(err) < 0.03) return 0;      // on course — steady as she goes
  const GAIN = 2.4;                        // full rudder by ~24° of bearing error
  return Math.max(-1, Math.min(1, err * GAIN));
}
